import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Home, Network, FolderKanban, Boxes, Bot, Users, CreditCard, Settings as SettingsIcon, LogOut, Menu, X, Shield } from "lucide-react";
import { Logo } from "../brand/Logo";
import { CreditBalance } from "../shared/CreditBalance";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { cn } from "../../lib/utils";

const NAV = [
  { to: "/app", label: "Architect", icon: Bot, testId: "nav-link-architect", end: true },
  { to: "/app/dashboard", label: "Panel", icon: Home, testId: "nav-link-dashboard" },
  { to: "/app/map", label: "Mapa", icon: Network, testId: "nav-link-map" },
  { to: "/app/projects", label: "Proyectos", icon: FolderKanban, testId: "nav-link-projects" },
  { to: "/app/systems", label: "Sistemas", icon: Boxes, testId: "nav-link-systems" },
  { to: "/app/plan", label: "Plan", icon: Network, testId: "nav-link-plan" },
];
const SECONDARY = [
  { to: "/app/team", label: "Equipo", icon: Users, testId: "nav-link-team" },
  { to: "/app/billing", label: "Facturación", icon: CreditCard, testId: "nav-link-billing" },
  { to: "/app/settings", label: "Ajustes", icon: SettingsIcon, testId: "nav-link-settings" },
];

function NavItem({ item, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      end={item.end}
      data-testid={item.testId}
      className={({ isActive }) => cn(
        "flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors",
        isActive ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                 : "text-slate-400 hover:text-white hover:bg-white/[0.04] border border-transparent"
      )}
    >
      <Icon className="h-4 w-4" />
      {item.label}
    </NavLink>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);
  const [plan, setPlan] = useState("free");
  const [open, setOpen] = useState(false);

  const loadCredits = async () => {
    try { const { data } = await api.get("/credits/balance"); setCredits(data.usable_credits ?? data.credits); setPlan(data.plan); } catch {}
  };
  useEffect(() => { loadCredits(); }, []);
  useEffect(() => {
    const h = () => loadCredits();
    window.addEventListener("credits:refresh", h);
    return () => window.removeEventListener("credits:refresh", h);
  }, []);

  const Sidebar = ({ onNav }) => (
    <div className="flex h-full flex-col">
      <div className="px-5 py-5"><Logo /></div>
      <nav className="flex-1 space-y-1 px-3">
        {NAV.map((i) => <NavItem key={i.to} item={i} onClick={onNav} />)}
        <div className="my-3 h-px bg-white/5" />
        {SECONDARY.map((i) => <NavItem key={i.to} item={i} onClick={onNav} />)}
        {user?.role === "admin" && (
          <NavItem item={{ to: "/admin", label: "Admin", icon: Shield, testId: "nav-link-admin" }} onClick={onNav} />
        )}
      </nav>
      <div className="border-t border-white/5 p-3">
        <div className="flex items-center gap-3 rounded-xl px-3 py-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-cyan-500/15 text-cyan-400 text-sm font-semibold">
            {(user?.name || user?.email || "U")[0].toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{user?.name || "Usuario"}</p>
            <p className="truncate text-xs text-slate-500">{user?.email}</p>
          </div>
          <button data-testid="logout-button" onClick={logout} className="text-slate-500 hover:text-red-400 transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen w-full bg-obsidian text-slate-100 overflow-hidden">
      <aside className="hidden md:block w-64 shrink-0 border-r border-white/10 bg-[#0B0D13]">
        <Sidebar />
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 border-r border-white/10 bg-[#0B0D13]">
            <button onClick={() => setOpen(false)} className="absolute right-1 top-1.5 inline-flex h-11 w-11 items-center justify-center text-slate-400" data-testid="mobile-nav-close" aria-label="Cerrar menú">
              <X className="h-5 w-5" />
            </button>
            <Sidebar onNav={() => setOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="glass sticky top-0 z-30 flex h-14 items-center justify-between border-b border-white/10 px-4 sm:px-6">
          <button className="md:hidden -ml-2.5 inline-flex h-11 w-11 items-center justify-center text-slate-300" onClick={() => setOpen(true)} data-testid="mobile-nav-open" aria-label="Abrir menú">
            <Menu className="h-5 w-5" />
          </button>
          <div className="md:hidden"><Logo withText={false} /></div>
          <div className="hidden md:block" />
          <div className="flex items-center gap-3">
            <CreditBalance credits={credits} plan={plan} />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <Outlet context={{ reloadCredits: loadCredits, credits }} />
          </div>
        </main>
      </div>
    </div>
  );
}
